const STORAGE_KEY = 'pocket-hell:scores:v2';
const PLAYER_KEY = 'pocket-hell:player:v2';
const CACHE_KEY = 'pocket-hell:community:v2';
const REPOSITORY = 'davidUSboy/pocket-hell';
const MAX_LOCAL_ENTRIES = 20;
const CACHE_LIFETIME = 5 * 60 * 1000;
const SCORE_PREFIX = '[POCKET HELL SCORE]';
const SCORE_MARKER = '<!-- POCKET_HELL_SCORE_V2 -->';
export class LeaderboardService {
    localEntries = this.readLocalEntries();
    communityEntries = this.readCommunityCache();
    getPlayerName() {
        try {
            return this.sanitizeName(localStorage.getItem(PLAYER_KEY) ?? 'PLAYER 1');
        }
        catch {
            return 'PLAYER 1';
        }
    }
    setPlayerName(value) {
        const name = this.sanitizeName(value);
        try {
            localStorage.setItem(PLAYER_KEY, name);
        }
        catch {
            // Storage can be unavailable in private or embedded browsers.
        }
        return name;
    }
    getLocalEntries() {
        return [...this.localEntries];
    }
    getCommunityEntries() {
        return [...this.communityEntries];
    }
    getBestLocal() {
        return this.localEntries[0] ?? null;
    }
    recordWin(result, playerName) {
        const entry = {
            id: result.runId,
            player: this.sanitizeName(playerName),
            score: result.score,
            time: result.timeSeconds,
            kills: result.kills,
            health: result.health,
            ammo: result.ammo,
            createdAt: new Date().toISOString(),
            source: 'local',
        };
        this.localEntries = [entry, ...this.localEntries.filter((item) => item.id !== entry.id)]
            .sort(this.compareEntries)
            .slice(0, MAX_LOCAL_ENTRIES);
        this.persistLocalEntries();
        return entry;
    }
    async fetchCommunity(force = false) {
        if (!force && this.communityEntries.length > 0) {
            return this.getCommunityEntries();
        }
        const endpoint = `https://api.github.com/repos/${REPOSITORY}/issues?state=all&per_page=100&sort=created&direction=desc`;
        const response = await fetch(endpoint, {
            headers: {
                Accept: 'application/vnd.github+json',
                'X-GitHub-Api-Version': '2022-11-28',
            },
        });
        if (!response.ok) {
            throw new Error(`GitHub API ${response.status}`);
        }
        const issues = (await response.json());
        this.communityEntries = issues
            .filter((issue) => !issue.pull_request)
            .map((issue) => this.parseIssue(issue))
            .filter((entry) => entry !== null)
            .sort(this.compareEntries)
            .slice(0, 25);
        this.persistCommunityCache();
        return this.getCommunityEntries();
    }
    createSubmissionUrl(entry, playerName) {
        const player = this.sanitizeName(playerName);
        const title = `${SCORE_PREFIX} ${player} — ${entry.score}`;
        const body = [
            SCORE_MARKER,
            '',
            '## Pocket Hell community score',
            '',
            `- **Player:** ${player}`,
            `- **Score:** ${entry.score}`,
            `- **Time:** ${entry.time.toFixed(1)}`,
            `- **Kills:** ${entry.kills}`,
            `- **Health:** ${entry.health}`,
            `- **Ammo:** ${entry.ammo}`,
            `- **Run ID:** \`${entry.id}\``,
            '- **Build:** 2.0.0',
            '',
            '> Generated by the live Pocket Hell build. Community scores use an honor system.',
        ].join('\n');
        return `https://github.com/${REPOSITORY}/issues/new?${new URLSearchParams({ title, body, labels: 'leaderboard' }).toString()}`;
    }
    parseIssue(issue) {
        if (!issue.title.startsWith(SCORE_PREFIX) || !issue.body?.includes(SCORE_MARKER)) {
            return null;
        }
        const readText = (label) => {
            const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const inlineMatch = issue.body?.match(new RegExp(`\\*\\*${escapedLabel}:\\*\\*\\s*(.+)$`, 'im'));
            const formMatch = issue.body?.match(new RegExp(`^###\\s+${escapedLabel}\\s*$\\n+([^\\n]+)`, 'im'));
            const value = inlineMatch?.[1] ?? formMatch?.[1];
            return value?.replace(/`/g, '').trim() ?? null;
        };
        const readNumber = (label) => {
            const value = Number(readText(label));
            return Number.isFinite(value) ? value : null;
        };
        const player = readText('Player');
        const score = readNumber('Score');
        const time = readNumber('Time');
        const kills = readNumber('Kills');
        const health = readNumber('Health');
        const ammo = readNumber('Ammo');
        if (!player || score === null || time === null || kills === null || health === null || ammo === null) {
            return null;
        }
        if (score < 0 || score > 999999 || time < 0 || time > 86400 || kills < 0 || kills > 99) {
            return null;
        }
        return {
            id: `github-${issue.id}`,
            player: this.sanitizeName(player),
            score: Math.round(score),
            time,
            kills: Math.round(kills),
            health: Math.round(health),
            ammo: Math.round(ammo),
            createdAt: issue.created_at,
            source: 'github',
            url: issue.html_url,
            githubUser: issue.user?.login,
        };
    }
    sanitizeName(value) {
        const name = value
            .normalize('NFKC')
            .toUpperCase()
            .replace(/[^A-Z0-9 _-]/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 16);
        return name || 'PLAYER 1';
    }
    compareEntries = (left, right) => right.score - left.score || left.time - right.time || left.createdAt.localeCompare(right.createdAt);
    readLocalEntries() {
        try {
            const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
            if (!Array.isArray(parsed))
                return [];
            return parsed
                .filter((entry) => this.isEntry(entry))
                .map((entry) => ({ ...entry, source: 'local' }))
                .sort(this.compareEntries)
                .slice(0, MAX_LOCAL_ENTRIES);
        }
        catch {
            return [];
        }
    }
    persistLocalEntries() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.localEntries));
        }
        catch {
            // The game still works when storage is blocked.
        }
    }
    readCommunityCache() {
        try {
            const cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) ?? 'null');
            if (!cache || Date.now() - cache.savedAt > CACHE_LIFETIME || !Array.isArray(cache.entries))
                return [];
            return cache.entries.filter((entry) => this.isEntry(entry));
        }
        catch {
            return [];
        }
    }
    persistCommunityCache() {
        try {
            const cache = { savedAt: Date.now(), entries: this.communityEntries };
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
        }
        catch {
            // Cache is optional.
        }
    }
    isEntry(value) {
        if (!value || typeof value !== 'object')
            return false;
        const entry = value;
        return (typeof entry.id === 'string' &&
            typeof entry.player === 'string' &&
            typeof entry.score === 'number' &&
            typeof entry.time === 'number' &&
            typeof entry.kills === 'number' &&
            typeof entry.health === 'number' &&
            typeof entry.ammo === 'number' &&
            typeof entry.createdAt === 'string');
    }
}
